import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import './QualityEvaluatorPrompt.scss';

export default function QualityEvaluatorPrompt() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuction();
  }, [auctionId]);

  // Redirect if inspection is not allowed or inspector type is not Private Inspector
  useEffect(() => {
    if (!auction || loading) return;
    const showPrompt =
      auction.inspection_allowed === 'Yes' && auction.inspector_type === 'Private Inspector';
    if (!showPrompt) {
      toast.info(
        'Quality evaluator is not available for this auction (Inspection must be Yes and Inspector type Private Inspector).',
        { autoClose: 4000 }
      );
      navigate(`/auction/${auctionId}/collection-delivery`, { replace: true });
    }
  }, [auction, loading, auctionId, navigate]);

  const fetchAuction = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (response && response.success) {
        setAuction(response.data);
      }
    } catch (error) {
      console.error('Error fetching auction:', error);
      toast.error('Failed to load auction details');
    } finally {
      setLoading(false);
    }
  };

  const handleYes = () => {
    // Navigate to Request Inspection Quotation page
    navigate(`/auction/${auctionId}/request-inspection-quotation`);
  };

  const handleNo = () => {
    // Redirect to Product Collection Status Update page
    navigate(`/auction/${auctionId}/collection-delivery`);
  };

  if (loading) {
    return <div className="quality-evaluator-prompt loading">Loading...</div>;
  }

  if (!auction) {
    return <div className="quality-evaluator-prompt error">Auction not found</div>;
  }

  const showPrompt =
    auction.inspection_allowed === 'Yes' && auction.inspector_type === 'Private Inspector';
  if (!showPrompt) {
    return (
      <div className="quality-evaluator-prompt loading">
        <p>Quality evaluator is not available for this auction.</p>
        <p className="redirect-reason">
          This auction has Inspection Allowed = {String(auction.inspection_allowed || 'N/A')} and
          Inspector Type = {String(auction.inspector_type || 'N/A')}. Both must be &quot;Yes&quot;
          and &quot;Private Inspector&quot; to recruit a quality evaluator.
        </p>
        <p>Redirecting to collection &amp; delivery…</p>
      </div>
    );
  }

  return (
    <div className="quality-evaluator-prompt">
      <div className="prompt-overlay">
        <div className="prompt-card">
          {/* Success Icon */}
          <div className="success-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#28a745"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          {/* Title */}
          <h1 className="prompt-title">Payment Completed Successfully!</h1>
          <p className="prompt-subtitle">Your payment has been processed successfully.</p>

          {/* Product Info */}
          <div className="product-info-card">
            <div className="product-name">{auction.product_name}</div>
            <div className="product-details">
              <span>Category: {auction.category_name || 'N/A'}</span>
              <span>Location: {auction.city || auction.country || 'N/A'}</span>
            </div>
          </div>

          {/* Prompt Question */}
          <div className="prompt-question">
            <h2>Would you like to recruit a Quality Evaluator?</h2>
            <p className="question-description">
              A quality evaluator can inspect the product before final acceptance to ensure it meets your requirements.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="btn-yes" onClick={handleYes}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Yes, Recruit Quality Evaluator
            </button>
            <button className="btn-no" onClick={handleNo}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              No, Skip
            </button>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>
              If you select "Yes", you'll be able to send inspection requests to qualified quality inspectors in your region.
              They will submit quotations, and you can select the best one for your needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
